import { useQuery } from "@tanstack/react-query";
import type { AuditCategory, AuditFilter, AuditOutcome } from "@zworks/console-contracts";
import { Download } from "lucide-react";
import * as React from "react";
import { EmptyState, ErrorState, ForbiddenState, LoadingState } from "@/components/states";
import { PageHeader } from "@/components/bespoke";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ApiError, auditExportPath, auditListQuery } from "@/lib/api";
import { useCurrentProject } from "@/lib/project";
import { formatDateTime } from "@/lib/format";

const filters: { value: AuditFilter; label: string }[] = [
  { value: "all", label: "All events" },
  { value: "security", label: "Security" },
  { value: "approvals", label: "Approvals" },
  { value: "members", label: "Members" },
  { value: "credits", label: "Credits" },
  { value: "runs", label: "Runs" },
];

const outcomeBadges: Record<
  AuditOutcome,
  { label: string; variant: "success" | "warning" | "danger" | "secondary" }
> = {
  started: { label: "Started", variant: "secondary" },
  succeeded: { label: "Succeeded", variant: "success" },
  denied: { label: "Denied", variant: "warning" },
  failed: { label: "Failed", variant: "danger" },
};

const categoryLabels: Record<AuditCategory, string> = {
  security: "Security",
  approvals: "Approvals",
  members: "Members",
  credits: "Credits",
  runs: "Runs",
};

/** A labelled outcome badge. Colour never carries the meaning alone. */
function OutcomeBadge({ outcome }: { outcome: AuditOutcome }) {
  const badge = outcomeBadges[outcome];
  return <Badge variant={badge.variant}>{badge.label}</Badge>;
}

/**
 * The page header. The export action appears only once a project is known, and it carries the
 * filter the page is showing, so the file is the range on screen.
 */
function AuditHeader({ exportHref }: { exportHref?: string }) {
  return (
    <PageHeader
      title="Audit log"
      actions={
        exportHref ? (
          <Button asChild variant="outline">
            <a href={exportHref} download>
              <Download aria-hidden />
              Export CSV
            </a>
          </Button>
        ) : null
      }
    />
  );
}

/**
 * The project's audit log, as the Audit board draws it: the category tabs, one table of time,
 * event, actor, target, and outcome, and the CSV export of the same range. Every entry is
 * append-only and written with the change it describes, so the log is the trusted record of what
 * happened. No credential, signed link, or raw prompt is ever recorded in it.
 */
export function AuditPage() {
  const { project, loaded } = useCurrentProject();
  const [category, setCategory] = React.useState<AuditFilter>("all");
  const [page, setPage] = React.useState(1);
  const list = useQuery({
    ...auditListQuery(project?.id ?? "", category, page),
    enabled: loaded && project !== null,
  });

  function selectCategory(next: AuditFilter) {
    setCategory(next);
    setPage(1);
  }

  const heading = <AuditHeader />;

  // A disabled query stays pending, so the empty project is decided before the query.
  if (!loaded || (project !== null && list.isPending)) {
    return (
      <>
        {heading}
        <LoadingState label="Loading audit log" rows={6} />
      </>
    );
  }

  if (project === null) {
    return (
      <>
        {heading}
        <EmptyState
          title="No projects yet"
          description="Create your first project. Its audit log appears here."
        />
      </>
    );
  }

  if (list.isError) {
    const error = list.error;
    return (
      <>
        {heading}
        {error instanceof ApiError && error.status === 403 ? (
          <ForbiddenState
            title="You do not have access to this project"
            description="Ask an owner to invite you. Nothing from the project is shown."
          />
        ) : (
          <ErrorState
            title="Audit log could not be loaded"
            description={
              error instanceof ApiError
                ? (error.problem?.detail ?? error.message)
                : "The request could not be completed."
            }
            requestId={error instanceof ApiError ? (error.requestId ?? "unknown") : "unknown"}
            onRetry={() => void list.refetch()}
          />
        )}
      </>
    );
  }

  // The project switch can disable the query mid-flight, so data is read once it exists.
  const data = list.data;
  if (!data) {
    return (
      <>
        {heading}
        <LoadingState label="Loading audit log" rows={6} />
      </>
    );
  }

  const totalPages = Math.max(1, Math.ceil(data.total / data.pageSize));
  const currentPage = Math.min(page, totalPages);
  const from = data.total === 0 ? 0 : (currentPage - 1) * data.pageSize + 1;
  const to = (currentPage - 1) * data.pageSize + data.entries.length;

  return (
    <div className="flex flex-col gap-6">
      <AuditHeader exportHref={auditExportPath(project.id, category)} />

      <Tabs value={category} onValueChange={(value) => selectCategory(value as AuditFilter)}>
        <TabsList aria-label="Filter audit entries by category">
          {filters.map((entry) => (
            <TabsTrigger key={entry.value} value={entry.value}>
              {entry.label}
            </TabsTrigger>
          ))}
        </TabsList>
      </Tabs>

      {data.entries.length === 0 ? (
        <EmptyState
          title={category === "all" ? "No audit entries yet" : `No ${category} entries yet`}
          description={
            category === "all"
              ? "Changes in this project will appear here as they happen."
              : "Nothing matches this filter. Try another category."
          }
        />
      ) : (
        <Card>
          <div className="overflow-x-auto">
            <table className="w-full border-collapse text-sm">
              <caption className="sr-only">Audit entries, newest first</caption>
              <thead>
                <tr>
                  {["Time", "Event", "Actor", "Target", "Outcome"].map((header) => (
                    <th
                      key={header}
                      scope="col"
                      className="h-10 border-b px-3 text-left text-[0.8125rem] font-medium whitespace-nowrap text-muted-foreground first:pl-5 last:pr-5"
                    >
                      {header}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {data.entries.map((entry) => (
                  <tr key={entry.id} className="group hover:bg-muted/50">
                    <td className="border-b px-3 py-2.5 align-middle whitespace-nowrap tabular-nums group-last:border-b-0 first:pl-5 last:pr-5">
                      {formatDateTime(entry.createdAt)}
                    </td>
                    <td className="border-b px-3 py-2.5 align-middle whitespace-nowrap group-last:border-b-0 first:pl-5 last:pr-5">
                      <div className="flex flex-col gap-0.5">
                        <span className="font-mono text-[0.8125rem] font-medium">
                          {entry.event}
                        </span>
                        <span className="text-xs text-muted-foreground">
                          {categoryLabels[entry.category]}
                        </span>
                      </div>
                    </td>
                    <td className="border-b px-3 py-2.5 align-middle whitespace-nowrap group-last:border-b-0 first:pl-5 last:pr-5">
                      {entry.actorName ?? "System"}
                    </td>
                    <td className="border-b px-3 py-2.5 align-middle whitespace-nowrap group-last:border-b-0 first:pl-5 last:pr-5 font-mono text-[0.8125rem]">
                      {entry.target ?? "–"}
                    </td>
                    <td className="border-b px-3 py-2.5 align-middle whitespace-nowrap group-last:border-b-0 first:pl-5 last:pr-5">
                      <OutcomeBadge outcome={entry.outcome} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="flex items-center justify-between gap-3 border-t px-5 py-3">
            <p className="text-[0.8125rem] text-muted-foreground tabular-nums">
              {from}–{to} of {data.total}
            </p>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                disabled={currentPage <= 1}
                onClick={() => setPage(currentPage - 1)}
              >
                Previous
              </Button>
              <Button
                variant="outline"
                size="sm"
                disabled={currentPage >= totalPages}
                onClick={() => setPage(currentPage + 1)}
              >
                Next
              </Button>
            </div>
          </div>
        </Card>
      )}

      <p className="text-[0.8125rem] text-muted-foreground">
        Entries are append-only. No credentials, signed links, or raw prompts are ever recorded.
      </p>
    </div>
  );
}
